/**
 * Per-kennel branding config. Built around the idea that this codebase serves
 * one kennel per deployment. To stand up a second kennel's site (e.g. AH3):
 *
 *   1. Duplicate this file → branding-ah3.ts (or similar) and edit values.
 *   2. Swap the import on the last line of THIS file.
 *   3. Replace public/<badge>.png + regenerate public/icons/* + favicon.ico.
 *   4. Edit index.html <title>, <meta description>, apple-mobile-web-app-title.
 *   5. Edit public/manifest.webmanifest name, short_name, description.
 *
 * (#3–5 are not React-bound so they can't read this config at runtime; they're
 * "fork-time" copies.)
 */

import type { CalendarSource } from "./types.js";
import { CALENDARS } from "./calendars.js";

export type CalendarGroup = { label: string; sourceIds: string[] };

export type ContactLink = {
  /** Display label, e.g. "Sign up for the mail list" */
  label: string;
  /** Full href — http(s), mailto:, tel:, etc. */
  href: string;
};

export type KennelBranding = {
  /** Stable id for env / debug purposes. */
  id: string;
  /** Full kennel name, e.g. "Honolulu Hawaii Hash House Harriers" */
  name: string;
  /** Short brand name for tight spots, e.g. "Honolulu H5" */
  shortName: string;
  /** PWA short_name (≤12 chars typical), e.g. "H5" */
  appShortName: string;
  /** Header subtitle / tagline */
  tagline: string;
  /** <meta name="description"> content */
  metaDescription: string;
  /** Hero blurb on the homepage */
  heroDescription: string;
  /** Path to the badge image used in the header (lives under /public) */
  badgePath: string;
  /** Public-facing contact paths (email signup, social, etc.) */
  contactLinks: ContactLink[];
  /**
   * Calendar grouping for the filter tabs. The first group is the home
   * kennel (used for next-trail logic, auto-expand-imminent-trail, etc.).
   * The "secondary" group is a related-but-distinct kennel that should get
   * its own filter tab. Anything in CALENDARS not in either of these falls
   * into "Sister kennels".
   */
  calendarPrimary: CalendarGroup;
  calendarSecondary: CalendarGroup;
  /**
   * Optional substring rewrites applied to event titles at fetch time. Lets
   * a kennel normalize its own naming quirks (e.g. maintainers writing
   * "Honolulu H4" when the canonical abbreviation is "H5"). Case-insensitive
   * literal-substring match; first match per rule, applied in order.
   */
  titleRewrites?: { from: string; to: string }[];
};

const h5: KennelBranding = {
  id: "h5",
  name: "Honolulu Hawaii Hash House Harriers",
  shortName: "Honolulu Hawaii H3",
  appShortName: "H5",
  tagline: "Hawaii's original weekday hash",
  metaDescription:
    "Honolulu Hawaii Hash House Harriers (H5) — Oahu's free Tuesday hash. Trails, locations, and a calendar you can subscribe to.",
  heroDescription:
    "Hawaii's longest-running hash. Adventurous, fun, easy going — come give it a try. Hash virgins and visitors always welcome.",
  badgePath: "/h5-badge.png",
  contactLinks: [
    {
      label: "Sign up for the mail list",
      href: "mailto:honolulu-hawaii-hash-house-harriers+subscribe@googlegroups.com",
    },
    {
      label: "Facebook group",
      href: "https://www.facebook.com/groups/231670710187037/",
    },
  ],
  calendarPrimary: { label: "H5", sourceIds: ["h5"] },
  calendarSecondary: {
    label: "AH3",
    sourceIds: [
      "ah3-main",
      "berzerk",
      "leeward-layabouts",
      "kailua-crew",
      "north-shore-thinking-drinking",
    ],
  },
  titleRewrites: [{ from: "Honolulu H4", to: "H5" }],
};

export const BRANDING: KennelBranding = h5;

/** All sister-kennel calendar sources — derived from CALENDARS minus the
 *  primary and secondary groups. Used by the "Sister kennels" filter tab. */
export function sisterKennelSources(): CalendarSource[] {
  const exclude = new Set([
    ...BRANDING.calendarPrimary.sourceIds,
    ...BRANDING.calendarSecondary.sourceIds,
  ]);
  return CALENDARS.filter((c) => !exclude.has(c.id));
}
